/**
 * Color Contrast Utilities for WCAG AA Compliance
 * Ensures text is always readable against its background
 */

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color code (with or without #)
 * @returns {object} RGB object {r, g, b}
 */
export function hexToRgb(hex) {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Handle shorthand hex (e.g., #fff)
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  
  const bigint = parseInt(hex, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  };
}

/**
 * Calculate relative luminance of a color (WCAG formula)
 * @param {object} rgb - RGB object {r, g, b}
 * @returns {number} Luminance value between 0 and 1
 */
export function getLuminance(rgb) {
  const { r, g, b } = rgb;
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors (WCAG formula)
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @returns {number} Contrast ratio (1 to 21)
 */
export function getContrastRatio(color1, color2) {
  const lum1 = getLuminance(hexToRgb(color1));
  const lum2 = getLuminance(hexToRgb(color2));
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if a color is considered "light" (luminance > 0.5)
 * @param {string} hex - Hex color code
 * @returns {boolean} True if color is light
 */
export function isLightColor(hex) {
  if (!hex || typeof hex !== 'string') return true;
  const rgb = hexToRgb(hex.replace(/^#/, ''));
  const luminance = getLuminance(rgb);
  return luminance > 0.45; // Slightly lower threshold for better detection
}

/**
 * Get the best contrasting text color for a given background
 * @param {string} bgColor - Background hex color
 * @param {object} options - Options for text colors
 * @returns {string} Best text color (hex)
 */
export function getContrastingTextColor(bgColor, options = {}) {
  const {
    lightText = '#ffffff',
    darkText = '#1f2937',
    mutedLightText = 'rgba(255,255,255,0.8)',
    mutedDarkText = 'rgba(31,41,55,0.8)'
  } = options;
  
  if (!bgColor || typeof bgColor !== 'string') {
    return darkText;
  }
  
  // Handle rgba colors
  if (bgColor.startsWith('rgba') || bgColor.startsWith('rgb')) {
    // Extract RGB values and check luminance
    const match = bgColor.match(/\d+/g);
    if (match && match.length >= 3) {
      const luminance = getLuminance({ r: +match[0], g: +match[1], b: +match[2] });
      return luminance > 0.45 ? darkText : lightText;
    }
    return darkText;
  }
  
  return isLightColor(bgColor) ? darkText : lightText;
}

/**
 * Ensures minimum contrast ratio is met, adjusting text color if needed
 * @param {string} textColor - Proposed text color
 * @param {string} bgColor - Background color
 * @param {number} minRatio - Minimum contrast ratio (default: 4.5 for WCAG AA)
 * @returns {string} Corrected text color
 */
export function ensureContrast(textColor, bgColor, minRatio = 4.5) {
  if (!bgColor || !textColor) return textColor;
  
  // Normalize colors
  const normalizedBg = bgColor.startsWith('#') ? bgColor : `#${bgColor}`;
  const normalizedText = textColor.startsWith('#') ? textColor : 
    (textColor.startsWith('rgba') || textColor.startsWith('rgb')) ? textColor : `#${textColor}`;
  
  // If text is rgba/rgb, we can't easily calculate contrast
  if (normalizedText.startsWith('rgb')) {
    const match = normalizedText.match(/\d+/g);
    if (match && match.length >= 3) {
      const textHex = rgbToHex(+match[0], +match[1], +match[2]);
      const ratio = getContrastRatio(textHex, normalizedBg);
      if (ratio >= minRatio) return textColor;
    }
    // Fall back to safe choice
    return getContrastingTextColor(normalizedBg);
  }
  
  try {
    const ratio = getContrastRatio(normalizedText, normalizedBg);
    if (ratio >= minRatio) return textColor;
    
    // Return opposite color if contrast is insufficient
    return getContrastingTextColor(normalizedBg);
  } catch {
    return getContrastingTextColor(normalizedBg);
  }
}

/**
 * Convert RGB to Hex
 */
export function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => {
    const hex = Math.min(255, Math.max(0, x)).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Darken a hex color by a percentage
 * @param {string} hex - Hex color
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} Darkened hex color
 */
export function darkenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  const factor = 1 - (percent / 100);
  return rgbToHex(
    Math.round(rgb.r * factor),
    Math.round(rgb.g * factor),
    Math.round(rgb.b * factor)
  );
}

/**
 * Lighten a hex color by a percentage
 * @param {string} hex - Hex color
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} Lightened hex color
 */
export function lightenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  const factor = percent / 100;
  return rgbToHex(
    Math.round(rgb.r + (255 - rgb.r) * factor),
    Math.round(rgb.g + (255 - rgb.g) * factor),
    Math.round(rgb.b + (255 - rgb.b) * factor)
  );
}

/**
 * Generate a complete color palette from brand colors
 * @param {object} brandColors - Brand colors {primary, secondary, accent}
 * @returns {object} Complete palette with derived colors
 */
export function generatePalette(brandColors = {}) {
  const primary = brandColors.primary || '#6366f1';
  const secondary = brandColors.secondary || '#00D68F';
  const accent = brandColors.accent || brandColors.secondary || '#f59e0b';
  
  return {
    // Primary variations
    primary,
    primaryLight: lightenColor(primary, 20),
    primaryDark: darkenColor(primary, 20),
    primaryBg: lightenColor(primary, 90), // Very light for backgrounds
    
    // Secondary variations
    secondary,
    secondaryLight: lightenColor(secondary, 20),
    secondaryDark: darkenColor(secondary, 20),
    
    // Accent
    accent,
    
    // Neutrals
    dark: '#0f0f1a',
    darkAlt: '#1a1a2e',
    light: '#ffffff',
    lightAlt: '#f8fafc',
    gray: '#64748b',
    
    // Text colors for different backgrounds
    textOnPrimary: isLightColor(primary) ? '#1f2937' : '#ffffff',
    textOnSecondary: isLightColor(secondary) ? '#1f2937' : '#ffffff',
    textOnDark: '#ffffff',
    textOnLight: '#1f2937',
    textMutedOnDark: 'rgba(255,255,255,0.7)',
    textMutedOnLight: 'rgba(31,41,55,0.7)',
  };
}

/**
 * Get section background and text colors with guaranteed contrast
 * @param {string} sectionType - Type of section (hero, benefits, etc.)
 * @param {number} sectionIndex - Index in the page (for alternation)
 * @param {object} brandColors - Brand colors
 * @param {string} preferredStyle - 'light' or 'dark'
 * @returns {object} Colors for the section
 */
export function getSectionColors(sectionType, sectionIndex, brandColors = {}, preferredStyle = 'auto') {
  const palette = generatePalette(brandColors);
  
  // Hero sections typically use primary color or dark
  if (sectionType === 'hero') {
    const isColoredHero = true; // Use brand color for hero
    if (isColoredHero) {
      return {
        background: `linear-gradient(135deg, ${palette.primaryDark} 0%, ${palette.primary} 100%)`,
        textColor: palette.textOnPrimary,
        mutedTextColor: isLightColor(palette.primary) ? 'rgba(31,41,55,0.7)' : 'rgba(255,255,255,0.8)',
        headlineColor: palette.textOnPrimary,
        ctaBg: palette.secondary,
        ctaText: palette.textOnSecondary,
        isDark: !isLightColor(palette.primary)
      };
    }
  }
  
  // CTA sections use secondary/accent color
  if (sectionType === 'cta' || sectionType === 'offer') {
    return {
      background: palette.secondary,
      textColor: palette.textOnSecondary,
      mutedTextColor: isLightColor(palette.secondary) ? 'rgba(31,41,55,0.7)' : 'rgba(255,255,255,0.8)',
      headlineColor: palette.textOnSecondary,
      ctaBg: palette.primary,
      ctaText: palette.textOnPrimary,
      isDark: !isLightColor(palette.secondary)
    };
  }
  
  // Alternate between light and tinted sections
  const isOddSection = sectionIndex % 2 === 1;
  const useTint = preferredStyle === 'dark' || (preferredStyle === 'auto' && isOddSection);
  
  if (useTint) {
    // Dark/tinted section with primary color accent
    return {
      background: `linear-gradient(180deg, ${palette.primary}10 0%, ${palette.dark} 100%)`,
      textColor: palette.textOnDark,
      mutedTextColor: palette.textMutedOnDark,
      headlineColor: '#ffffff',
      ctaBg: palette.secondary,
      ctaText: palette.textOnSecondary,
      isDark: true
    };
  }
  
  // Light section
  return {
    background: palette.light,
    textColor: palette.textOnLight,
    mutedTextColor: palette.textMutedOnLight,
    headlineColor: '#111827',
    ctaBg: palette.primary,
    ctaText: palette.textOnPrimary,
    isDark: false
  };
}

export default {
  hexToRgb,
  getLuminance,
  getContrastRatio,
  isLightColor,
  getContrastingTextColor,
  ensureContrast,
  rgbToHex,
  darkenColor,
  lightenColor,
  generatePalette,
  getSectionColors
};
